import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { styled } from "@mui/material/styles";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import API_BASE_URL from "config/api";
import { BlueButton } from "styles";
import {
  WAF_PROVIDERS,
  buildLoaderScript,
  buildReadinessSteps,
  fetchIntegrationReadiness,
  normalizeReadiness,
} from "services/integrationReadiness";

const CodeBlock = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  overflowX: "auto",
  margin: `${theme.spacing(2)} 0`,
  fontSize: "0.875rem",
  fontFamily: "monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));

const PrimaryActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
}));

const SecondaryActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[700]}`,
  color: theme.palette.grey[900],
  fontWeight: 600,
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[900],
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[600],
  },
}));

const getSites = (me) =>
  (me?.user?.clients || []).flatMap((client) =>
    (client.sites || []).map((site) => ({
      ...site,
      tenantId: client.clientId,
    }))
  );

const providerLabel = (value) =>
  WAF_PROVIDERS.find((provider) => provider.value === value)?.label || "güvenlik duvarınız";

const observationLabel = (observation) => {
  const domain = observation?.domain ? ` (${observation.domain})` : "";
  return `${observation?.name || "Bilinmeyen çerez"}${domain}`;
};

function ChangeList({ title, items, color }) {
  if (!items.length) return null;
  return (
    <Box>
      <Typography variant="subtitle2" color={color} fontWeight="bold" mb={0.5}>
        {title}
      </Typography>
      <Stack component="ul" spacing={0.25} sx={{ pl: 2.5, my: 0 }}>
        {items.slice(0, 6).map((item) => (
          <Typography
            component="li"
            variant="body2"
            key={`${item.name}-${item.domain}-${item.path}`}
            sx={{ overflowWrap: "anywhere" }}
          >
            {observationLabel(item)}
          </Typography>
        ))}
      </Stack>
      {items.length > 6 && (
        <Typography variant="caption" color="text.secondary">
          +{items.length - 6} kayıt daha
        </Typography>
      )}
    </Box>
  );
}

function LogicookieIntegration() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [readinessError, setReadinessError] = useState("");
  const [loadingSites, setLoadingSites] = useState(true);
  const [checking, setChecking] = useState(false);
  const [startingScan, setStartingScan] = useState(false);
  const [selectedWaf, setSelectedWaf] = useState("");
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const selectedSiteIdRef = useRef("");
  const readinessRequestRef = useRef(0);
  const readinessAbortRef = useRef(null);

  selectedSiteIdRef.current = selectedSiteId;

  const selectedSite = useMemo(
    () => sites.find((site) => String(site.siteId) === String(selectedSiteId)),
    [selectedSiteId, sites]
  );
  const state = useMemo(() => normalizeReadiness(readiness), [readiness]);
  const steps = useMemo(() => buildReadinessSteps(state), [state]);
  const loaderScript = buildLoaderScript();

  useEffect(() => {
    let cancelled = false;
    const loadSites = async () => {
      try {
        const cached = sessionStorage.getItem("userMeData");
        let me = cached ? JSON.parse(cached) : null;
        if (!me) {
          const response = await fetch(`${API_BASE_URL}/me`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("authToken")}` },
          });
          if (!response.ok) throw new Error("Site bilgileri alınamadı");
          me = await response.json();
          sessionStorage.setItem("userMeData", JSON.stringify(me));
        }

        if (cancelled) return;
        const availableSites = getSites(me);
        const requestedSiteId = new URLSearchParams(location.search).get("siteId");
        const requestedSiteExists = availableSites.some(
          (site) => String(site.siteId) === String(requestedSiteId)
        );
        setSites(availableSites);
        setSelectedSiteId(
          requestedSiteExists
            ? String(requestedSiteId)
            : String(availableSites[0]?.siteId || "")
        );
      } catch (error) {
        if (!cancelled) {
          setNotice({ open: true, message: error.message, severity: "error" });
        }
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    };
    loadSites();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const loadReadiness = useCallback(async ({ quiet = false } = {}) => {
    if (!selectedSiteId) return;
    const requestedSiteId = selectedSiteId;
    const requestId = readinessRequestRef.current + 1;
    readinessRequestRef.current = requestId;
    readinessAbortRef.current?.abort();
    const controller = new AbortController();
    readinessAbortRef.current = controller;
    if (!quiet) {
      setChecking(true);
      setReadinessError("");
    }
    try {
      const result = await fetchIntegrationReadiness(
        requestedSiteId,
        sessionStorage.getItem("authToken"),
        controller.signal
      );
      if (
        requestId !== readinessRequestRef.current ||
        requestedSiteId !== selectedSiteIdRef.current
      ) return;
      setReadiness(result);
      setReadinessError("");
      if (result.access.wafProvider) setSelectedWaf(result.access.wafProvider);
    } catch (error) {
      if (error.name === "AbortError") return;
      if (
        requestId !== readinessRequestRef.current ||
        requestedSiteId !== selectedSiteIdRef.current
      ) return;
      setReadiness(null);
      setReadinessError(error.message);
      setNotice({ open: true, message: error.message, severity: "error" });
    } finally {
      if (
        requestId === readinessRequestRef.current &&
        requestedSiteId === selectedSiteIdRef.current
      ) setChecking(false);
    }
  }, [selectedSiteId]);

  useEffect(() => () => readinessAbortRef.current?.abort(), []);

  useEffect(() => {
    setReadiness(null);
    setReadinessError("");
    setSelectedWaf("");
    loadReadiness();
  }, [loadReadiness]);

  useEffect(() => {
    if (state.stage !== "scan_in_progress") return undefined;
    let cancelled = false;
    let timeoutId;
    const poll = async () => {
      await loadReadiness({ quiet: true });
      if (!cancelled) timeoutId = window.setTimeout(poll, 5000);
    };
    timeoutId = window.setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [loadReadiness, state.stage]);

  const startScan = async () => {
    if (!selectedSite || !state.integration.scanPrerequisitesMet) return;
    const requestedSiteId = String(selectedSite.siteId);
    setStartingScan(true);
    try {
      const response = await fetch(`${API_BASE_URL}/scan/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          siteId: selectedSite.siteId,
          requestDomain: selectedSite.domain,
        }),
      });
      const responseText = await response.text();
      let payload = {};
      try {
        payload = responseText ? JSON.parse(responseText) : {};
      } catch (_) {
        payload = { message: responseText };
      }
      if (!response.ok) {
        throw new Error(payload.message || payload.error || "Tarama başlatılamadı");
      }
      setNotice({ open: true, message: "Çerez taraması başlatıldı.", severity: "success" });
      if (selectedSiteIdRef.current === requestedSiteId) {
        await loadReadiness({ quiet: true });
      }
    } catch (error) {
      setNotice({ open: true, message: error.message, severity: "error" });
    } finally {
      setStartingScan(false);
    }
  };

  const renderCurrentAction = () => {
    if (checking) {
      return (
        <Box py={5} display="flex" justifyContent="center">
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (readinessError) {
      return (
        <Stack spacing={2}>
          <Alert severity="error">{readinessError}</Alert>
          <Box>
            <BlueButton onClick={() => loadReadiness()} startIcon={<RefreshIcon />}>
              Durumu yeniden kontrol et
            </BlueButton>
          </Box>
        </Stack>
      );
    }

    if (!readiness) return null;

    if (state.stage === "unknown") {
      return (
        <Alert severity="error">
          Entegrasyon durumu tanınamadı. Kurulum kodu güvenlik amacıyla kapalı tutuldu.
        </Alert>
      );
    }

    if (state.stage === "resolve_access") {
      const waf = state.access.wafProvider || selectedWaf;
      return (
        <Stack spacing={2}>
          <Alert severity="warning">
            Kurulum kodunu doğrularken siteye erişemedik. Kodu aşağıdaki sırayla yerleştirin;
            ardından genel güvenlik korumasını kapatmadan yalnız çerez tarama isteği için dar
            kapsamlı bir istisna oluşturun.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Logicookie kodu sitenizin <strong>&lt;head&gt;</strong> bölümünde Google Tag Manager,
            Analytics ve diğer ölçüm scriptlerinden önce bulunmalıdır.
          </Typography>
          <CodeBlock>{loaderScript}</CodeBlock>
          <CopyToClipboard
            text={loaderScript}
            onCopy={() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
          >
            <SecondaryActionButton startIcon={<Icon>content_copy</Icon>}>
              {copied ? "Kopyalandı" : "Kodu kopyala"}
            </SecondaryActionButton>
          </CopyToClipboard>
          {state.access.needsProviderSelection && (
            <FormControl fullWidth>
              <InputLabel id="waf-provider-label">Güvenlik duvarı</InputLabel>
              <Select
                labelId="waf-provider-label"
                label="Güvenlik duvarı"
                value={selectedWaf}
                onChange={(event) => setSelectedWaf(event.target.value)}
              >
                {WAF_PROVIDERS.map((provider) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {waf && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {providerLabel(waf)} için kontrol
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Bot yönetimi, rate limit ve managed challenge kayıtlarında Logicookie tarama
                isteğini kontrol edin. Sabit crawler IP’si API tarafından doğrulanmadan bu ekran
                bir IP adresi üretmez veya geniş kapsamlı allowlist önermez.
              </Typography>
            </Box>
          )}
          <Box>
            <BlueButton onClick={() => loadReadiness()} startIcon={<RefreshIcon />}>
              Erişimi yeniden kontrol et
            </BlueButton>
          </Box>
        </Stack>
      );
    }

    if (state.stage === "install_loader") {
      return (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Bu kodu sitenizin <strong>&lt;head&gt;</strong> bölümünde Google Tag Manager,
            Analytics ve diğer ölçüm scriptlerinden önce ekleyin. Tarama, Logicookie loader
            sitede doğrulanmadan başlamaz. Site ve müşteri kimlikleri Google Consent adımında
            oluşturulan GTM kodunda tanımlanır.
          </Typography>
          <Alert severity="info">
            <strong>CSP kullanıyorsanız:</strong> <code>script-src</code> politikasında
            {" "}<code>https://cdn.logicookie.com</code>, <code>connect-src</code> politikasında
            {" "}<code>https://api.logicookie.com</code> izinli olmalıdır. WAF veya bot koruması
            taramayı engellerse genel güvenlik duvarını kapatmayın; kurulum doğrulaması sonrasında
            bu sihirbaz dar kapsamlı istisna için sağlayıcıya özel yönlendirme gösterir.
          </Alert>
          <CodeBlock>{loaderScript}</CodeBlock>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <CopyToClipboard
              text={loaderScript}
              onCopy={() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }}
            >
              <PrimaryActionButton startIcon={<Icon>content_copy</Icon>}>
                {copied ? "Kopyalandı" : "Kodu kopyala"}
              </PrimaryActionButton>
            </CopyToClipboard>
            <SecondaryActionButton onClick={() => loadReadiness()} startIcon={<RefreshIcon />}>
              Kurulumu doğrula
            </SecondaryActionButton>
          </Stack>
        </Stack>
      );
    }

    if (state.stage === "configure_banner") {
      return (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Tarama sonuçlarının doğru dil ve kategori metinleriyle kaydedilebilmesi için önce
            banner dil ayarlarını tamamlayın.
          </Typography>
          <Box>
            <BlueButton
              onClick={() => navigate(`/configuration/language?siteId=${selectedSiteId}`)}
            >
              Banner ayarlarına git
            </BlueButton>
          </Box>
        </Stack>
      );
    }

    if (state.stage === "scan_catalog") {
      return (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Kurulum kodu ve banner ayarları doğrulandı. Siteyi gerçek bir ziyaretçi gibi
            tarayarak başlangıç kataloğunu oluşturun.
          </Typography>
          <Box>
            <BlueButton
              onClick={startScan}
              disabled={startingScan || !state.integration.scanPrerequisitesMet}
              startIcon={startingScan ? <CircularProgress size={16} color="inherit" /> : <Icon>travel_explore</Icon>}
            >
              {startingScan ? "Tarama başlatılıyor" : "Çerezleri tara"}
            </BlueButton>
          </Box>
        </Stack>
      );
    }

    if (state.stage === "scan_in_progress") {
      return (
        <Alert severity="info" icon={<CircularProgress size={20} />}>
          Tarama devam ediyor. Sonuç hazır olduğunda bu ekran otomatik güncellenecek.
        </Alert>
      );
    }

    if (state.stage === "review_catalog") {
      return (
        <Stack spacing={2}>
          <Alert severity="warning">
            İlk taramada bulunan {state.catalog.flaggedCount} çerez henüz doğrulanmadı.
            Google Consent adımına geçmeden önce bu kayıtların kategori ve açıklamalarını
            tamamlayın.
          </Alert>
          <Box>
            <BlueButton
              onClick={() => navigate(
                `/cookies/categories?siteId=${selectedSiteId}&lang=${selectedSite.defaultLanguage || "tr"}`
              )}
            >
              Kataloğu incele
            </BlueButton>
          </Box>
        </Stack>
      );
    }

    if (state.stage === "catalog_changes") {
      const { changes } = state.catalog;
      return (
        <Stack spacing={2}>
          <Alert severity="info">
            Son taramada {changes.addedCount} yeni, {changes.removedCount} artık gözlenmeyen
            çerez bulundu. Bu değişiklikler mevcut entegrasyonu durdurmaz.
          </Alert>
          <ChangeList title="Yeni gözlenenler" items={changes.added} color="success.main" />
          <ChangeList
            title="Bu taramada gözlenmeyenler"
            items={changes.removed}
            color="warning.main"
          />
          <Typography variant="caption" color="text.secondary">
            “Gözlenmedi” sonucu çerezin kesin olarak kaldırıldığı anlamına gelmez; koşullu
            çalışan etiketleri katalogdan çıkarmadan önce kontrol edin.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <PrimaryActionButton
              onClick={() => navigate(
                `/cookies/categories?siteId=${selectedSiteId}&lang=${selectedSite.defaultLanguage || "tr"}`
              )}
            >
              Kataloğu görüntüle
            </PrimaryActionButton>
            <SecondaryActionButton
              onClick={() => navigate(`/integrations/google-consent?siteId=${selectedSiteId}`)}
              disabled={!state.integration.canShowGtm}
            >
              Google Consent ayarları
            </SecondaryActionButton>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {state.catalog.observedCookieCount === 0 ? (
          <Alert severity="warning">
            Tarama tamamlandı ancak Logicookie dışındaki bir çerez gözlenmedi. Henüz
            yayınlanmamış veya yalnız belirli işlemlerde çalışan etiketleri kataloğa elle
            ekleyebilirsiniz.
          </Alert>
        ) : (
          <Alert severity="success">
            Başlangıç kataloğu hazır: {state.catalog.observedCookieCount} çerez gözlemi.
          </Alert>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <SecondaryActionButton
            onClick={() => navigate(
              `/cookies/categories?siteId=${selectedSiteId}&lang=${selectedSite.defaultLanguage || "tr"}`
            )}
          >
            Kataloğu görüntüle / çerez ekle
          </SecondaryActionButton>
          <SecondaryActionButton
            onClick={startScan}
            disabled={startingScan || !state.integration.scanPrerequisitesMet}
            startIcon={startingScan ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Yeniden tara
          </SecondaryActionButton>
          <PrimaryActionButton
            onClick={() => navigate(`/integrations/google-consent?siteId=${selectedSiteId}`)}
            disabled={!state.integration.canShowGtm}
          >
            Google Consent ayarları
          </PrimaryActionButton>
        </Stack>
      </Stack>
    );
  };

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} xl={10}>
            <Card sx={{ boxShadow: 3 }}>
              <Box p={{ xs: 2, md: 3 }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  gap={2}
                  mb={3}
                >
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      Logicookie Entegrasyonu
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      Kurulumu doğru sırayla tamamlayın ve her adımı sistem üzerinden doğrulayın.
                    </Typography>
                  </Box>
                  <Box sx={{ width: { xs: "100%", md: 320 }, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                      Web sitesi
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <Select
                      value={selectedSiteId}
                      onChange={(event) => setSelectedSiteId(String(event.target.value))}
                      IconComponent={KeyboardArrowDownIcon}
                      inputProps={{ "aria-label": "Web sitesi" }}
                      sx={{ minHeight: 44, bgcolor: "background.paper" }}
                    >
                      {sites.map((site) => (
                        <MenuItem key={site.siteId} value={String(site.siteId)}>
                          {site.domain}
                        </MenuItem>
                      ))}
                    </Select>
                    </FormControl>
                  </Box>
                </Box>

                {loadingSites ? (
                  <Box py={8} display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                ) : sites.length === 0 ? (
                  <Alert severity="info">Entegrasyon için önce bir site ekleyin.</Alert>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Stack spacing={0} role="list" aria-label="Entegrasyon adımları">
                        {steps.map((step, index) => (
                          <Box
                            key={step.key}
                            role="listitem"
                            display="flex"
                            gap={1.5}
                            py={1.75}
                            px={1}
                            borderBottom={index === steps.length - 1 ? 0 : "1px solid"}
                            borderColor="divider"
                            bgcolor={step.active ? "action.hover" : "transparent"}
                          >
                            {step.complete ? (
                              <CheckCircleOutlineIcon color="success" />
                            ) : (
                              <RadioButtonUncheckedIcon color={step.active ? "primary" : "disabled"} />
                            )}
                            <Box minWidth={0}>
                              <Typography variant="body2" fontWeight={step.active ? "bold" : "medium"}>
                                {step.label}
                              </Typography>
                              {step.key === "catalog" && (
                                state.catalog.hasCompletedScan || state.catalog.hasSuccessfulScan
                              ) && (
                                <Typography variant="caption" color="text.secondary">
                                  {state.catalog.observedCookieCount} gözlem
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box
                        minHeight={260}
                        borderLeft={{ xs: 0, md: "1px solid" }}
                        borderTop={{ xs: "1px solid", md: 0 }}
                        borderColor="divider"
                        pl={{ xs: 0, md: 3 }}
                        pt={{ xs: 3, md: 0 }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                          <Typography variant="h6" fontWeight="bold">
                            {steps.find((step) => step.active)?.label || "Entegrasyon hazır"}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => loadReadiness()}
                            disabled={checking}
                            aria-label="Durumu yenile"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Box>
                        {renderCurrentAction()}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={notice.open}
        autoHideDuration={6000}
        onClose={() => setNotice((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={notice.severity}
          onClose={() => setNotice((current) => ({ ...current, open: false }))}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default LogicookieIntegration;
